/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-finding-investors-form',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './finding-investors-form.component.html',
  styleUrl: './finding-investors-form.component.scss',
})
export class FindingInvestorsFormComponent implements OnInit {
  @Input() initialValue: any;
  @Output() formSubmit = new EventEmitter<any>();

  findingInvestorsForm: FormGroup;
  submitAttempted = false;

  investmentTypes = ['Equity', 'Debt', 'Revenue Sharing'];

  private investmentTypeMap: Record<string, string> = {
    Equity: 'equity',
    Debt: 'debt',
    'Revenue Sharing': 'revenue_sharing',
  };

  constructor(private fb: FormBuilder) {
    this.findingInvestorsForm = this.fb.group({
      knowInvestorType: ['', Validators.required],
      investmentTypes: this.fb.group(
        this.investmentTypes.reduce((acc, type) => {
          acc[this.sanitizeKey(type)] = [false];
          return acc;
        }, {} as any),
      ),
      willingToRelocate: ['', Validators.required],
    });

    this.setupConditionalValidation();
  }

  ngOnInit(): void {
    if (this.initialValue) {
      const patchData = { ...this.initialValue };

      // Convert selectedInvestmentTypes array back to investmentTypes checkbox object
      if (Array.isArray(patchData.selectedInvestmentTypes)) {
        const investmentTypesObj: Record<string, boolean> = {};
        this.investmentTypes.forEach((type) => {
          const key = this.sanitizeKey(type);
          const backendValue = this.investmentTypeMap[type];
          investmentTypesObj[key] =
            patchData.selectedInvestmentTypes.includes(backendValue);
        });
        patchData.investmentTypes = investmentTypesObj;
      }

      this.findingInvestorsForm.patchValue(patchData);
    }
  }

  private setupConditionalValidation() {
    this.findingInvestorsForm
      .get('knowInvestorType')
      ?.valueChanges.subscribe((value) => {
        const investmentTypesControl =
          this.findingInvestorsForm.get('investmentTypes');

        if (value === 'yes') {
          investmentTypesControl?.setValidators([
            this.atLeastOneCheckboxValidator(),
          ]);
        } else {
          investmentTypesControl?.clearValidators();
          // Reset checkboxes when hiding
          this.investmentTypes.forEach((type) => {
            investmentTypesControl
              ?.get(this.sanitizeKey(type))
              ?.setValue(false);
          });
        }
        investmentTypesControl?.updateValueAndValidity();
      });
  }

  sanitizeKey(type: string): string {
    return type.toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  atLeastOneCheckboxValidator() {
    return (group: any) => {
      const values = Object.values(group.value || {});
      const hasAtLeastOne = values.some((val) => val === true);
      return hasAtLeastOne ? null : { required: true };
    };
  }

  get hasErrors(): boolean {
    return this.submitAttempted && this.findingInvestorsForm.invalid;
  }

  public onSubmitForm() {
    if (this.findingInvestorsForm.valid) {
      const formValue = this.findingInvestorsForm.value;
      const selectedInvestmentTypes = this.extractSelectedInvestmentTypes(
        formValue.investmentTypes,
      );
      this.formSubmit.emit({
        ...formValue,
        selectedInvestmentTypes,
      });
    } else {
      this.submitAttempted = true;
      this.markFormGroupTouched(this.findingInvestorsForm);
    }
  }

  private extractSelectedInvestmentTypes(
    investmentTypes: Record<string, boolean>,
  ): string[] {
    if (!investmentTypes) return [];

    return Object.entries(investmentTypes)
      .filter(([, selected]) => selected)
      .map(([key]) => {
        const displayLabel = this.investmentTypes.find(
          (type) => this.sanitizeKey(type) === key,
        );
        return displayLabel ? this.investmentTypeMap[displayLabel] : key;
      });
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }
}
